import {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
  JSX,
  useRef,
  useCallback,
} from 'react';
import { TourProvider, useTour } from '@reactour/tour';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { useTourAutoPlay } from './useTourAutoPlay';
import { useEventTracking } from '../../../hooks/useEventTracking';
import { incrementDismissCount, markTourAsSeenAutoplay } from './tourUtils';
import {
  ASSISTANT_PANEL_ATTRIBUTE,
  isAssistantPanelOpen,
} from 'src/utils/assistantPanel';

/** Follow the Assistant panel's body attribute (PA-19). */
const useAssistantPanelOpen = (): boolean => {
  const [open, setOpen] = useState(isAssistantPanelOpen);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const read = (): void => setOpen(isAssistantPanelOpen());
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: [ASSISTANT_PANEL_ATTRIBUTE],
    });
    read();
    return () => observer.disconnect();
  }, []);

  return open;
};

// Create context for tour communication
const TourContext = createContext<{ startTour: () => void } | undefined>(
  undefined
);

export const useTourContext = (): { startTour: () => void } => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a BaseTour');
  }
  return context;
};

interface BaseTourProps {
  children: ReactNode;
  steps: Array<{
    selector: string;
    content: ReactNode;
    position?: string;
    styles?: Record<string, unknown>;
    action?: (elem: Element) => void;
    padding?: Record<string, number>;
  }>;
  tourDataKey: string;
  onTourEnd?: () => void;
  onTourClose?: () => void;
  initialIsOpen?: boolean;
}

// Simple tour component using useTour hook
const SimpleTour = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): null => {
  const { setIsOpen, setSteps, setCurrentStep } = useTour();

  useEffect((): void => {
    if (isOpen) {
      setCurrentStep(0);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [isOpen, setSteps, setCurrentStep, setIsOpen]);

  useEffect(() => {
    const handleTourClose = (): void => {
      onClose();
    };

    // Listen for tour close events
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        handleTourClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return null; // This component doesn't render anything, it just controls the tour
};

const BaseTour = ({
  children,
  steps,
  tourDataKey,
  onTourEnd,
  onTourClose,
  initialIsOpen = false,
}: BaseTourProps): JSX.Element => {
  const [isTourOpen, setIsTourOpen] = useState(initialIsOpen);
  const [currentStep, setCurrentStep] = useState(0);
  // PA-19 — a tour must not walk over a conversation the user is reading.
  const assistantPanelOpen = useAssistantPanelOpen();

  // Use ref to track step start time without triggering re-renders
  const stepStartTimeRef = useRef<number>(Date.now());

  // Tour tracking
  const {
    trackTourStart,
    trackTourComplete,
    trackTourStepAdvance,
    trackTourStepBack,
    trackTourClose,
  } = useEventTracking();

  // Extract tour name from tourDataKey
  const tourName = tourDataKey.replace('TourData', '').toLowerCase();

  // Watch for changes to initialIsOpen prop
  useEffect(() => {
    if (initialIsOpen) {
      setIsTourOpen(true);
    }
  }, [initialIsOpen]);

  // Track tour start
  useEffect(() => {
    if (isTourOpen && currentStep === 0) {
      // Mark that user has seen autoplay for this tour
      markTourAsSeenAutoplay(tourDataKey);

      // Extract step name from first step content
      const firstStepContent = steps[0]?.content;
      let stepName = 'Tour Introduction';

      // Try to extract title from TourSection component
      if (
        firstStepContent &&
        typeof firstStepContent === 'object' &&
        'props' in firstStepContent
      ) {
        const contentProps = firstStepContent.props;
        if (
          contentProps &&
          typeof contentProps === 'object' &&
          'title' in contentProps
        ) {
          stepName = contentProps.title;
        }
      }

      trackTourStart(tourName, steps.length, stepName, 'BaseTour', 'useEffect');
    }
  }, [
    isTourOpen,
    currentStep,
    tourName,
    steps.length,
    steps,
    trackTourStart,
    tourDataKey,
  ]);

  // Track step changes
  useEffect(() => {
    if (isTourOpen && currentStep > 0) {
      // Reset step start time for new step
      stepStartTimeRef.current = Date.now();
    }
  }, [currentStep, isTourOpen, steps.length]);

  // Add event listener for tour exit
  useEffect(() => {
    const handleTourExit = (): void => {
      setIsTourOpen(false);
    };

    document.addEventListener('tour-exit', handleTourExit);

    return () => {
      document.removeEventListener('tour-exit', handleTourExit);
    };
  }, []);

  const handleTourEnd = useCallback((): void => {
    // Extract step name from current step content
    const currentStepContent = steps[currentStep]?.content;
    let stepName = 'Unknown Step';

    // Try to extract title from TourSection component
    if (
      currentStepContent &&
      typeof currentStepContent === 'object' &&
      'props' in currentStepContent
    ) {
      const contentProps = currentStepContent.props;
      if (
        contentProps &&
        typeof contentProps === 'object' &&
        'title' in contentProps
      ) {
        stepName = contentProps.title;
      }
    }

    // Track tour completion
    trackTourComplete(
      tourName,
      steps.length,
      stepName,
      'BaseTour',
      'handleTourEnd'
    );

    // Increment dismissal count when tour ends
    incrementDismissCount(tourDataKey);
    setIsTourOpen(false);

    if (onTourEnd) {
      onTourEnd();
    }
  }, [tourName, tourDataKey, onTourEnd, currentStep, steps, trackTourComplete]);

  // Memoize functions to prevent unnecessary re-renders
  const handleNextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, steps.length]);

  const handleEndTour = useCallback(() => {
    handleTourEnd();
  }, [handleTourEnd]);

  // Auto-play hook
  const { isAutoPlaying, isPaused, timeRemaining, togglePause } =
    useTourAutoPlay({
      // PA-19 — paused while the Assistant panel is open: the timer is what walked the
      // tour onto the panel in the first place.
      isOpen: isTourOpen && !assistantPanelOpen,
      currentStep,
      totalSteps: steps.length,
      onNextStep: handleNextStep,
      onEndTour: handleEndTour,
    });

  const handleClose = (): void => {
    // Extract step name from current step content
    const currentStepContent = steps[currentStep]?.content;
    let stepName = 'Unknown Step';

    // Try to extract title from TourSection component
    if (
      currentStepContent &&
      typeof currentStepContent === 'object' &&
      'props' in currentStepContent
    ) {
      const contentProps = currentStepContent.props;
      if (
        contentProps &&
        typeof contentProps === 'object' &&
        'title' in contentProps
      ) {
        stepName = contentProps.title;
      }
    }

    // Track tour close
    trackTourClose(
      tourName,
      currentStep,
      currentStep,
      stepName,
      'BaseTour',
      'handleClose'
    );

    // Increment dismissal count when user manually closes tour
    incrementDismissCount(tourDataKey);
    setIsTourOpen(false);

    if (onTourClose) {
      onTourClose();
    }
  };

  const startTour = (): void => {
    // Track tour start
    stepStartTimeRef.current = Date.now();

    setIsTourOpen(true);
  };

  const contextValue = {
    startTour,
  };

  return (
    <TourContext.Provider value={contextValue}>
      <TourProvider
        steps={steps}
        isOpen={isTourOpen}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        onRequestClose={handleClose}
        onClickMask={handleClose}
        onClickClose={handleClose}
        onClickNext={(e) => {
          if (e.currentStep === steps.length - 1) {
            handleTourEnd();
          }
        }}
        onClickPrev={() => {
          handleClose();
        }}
        showNavigation={true}
        showBadge={true}
        showDots={true}
        showNavigationNumber={true}
        showPrevNextButtons={true}
        showCloseButton={true}
        disableInteraction={false}
        disableDotsNavigation={false}
        disableKeyboardNavigation={false}
        padding={{ mask: [10, 10] }}
        position="bottom"
        inViewThreshold={0}
        styles={{
          popover: (base) => ({
            ...base,
            backgroundColor: 'white',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            zIndex: 99999,
            maxWidth: '500px',
            padding: '20px',
            marginTop: '20px', // Add margin to position tour lower
          }),
          badge: (base) => ({
            ...base,
            backgroundColor: '#1976d2',
          }),
          controls: (base) => ({
            ...base,
            marginTop: 16,
          }),
          navigation: (base) => ({
            ...base,
            backgroundColor: '#1976d2',
            color: 'white',
          }),
          arrow: (base) => ({
            ...base,
            color: '#1976d2',
          }),
          close: (base) => ({
            ...base,
            color: '#666',
            '&:hover': {
              color: '#1976d2',
            },
          }),
          mask: (base) => ({
            ...base,
            zIndex: 99998,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }),
        }}
        components={{
          Navigation: ({
            currentStep,
            steps,
            setCurrentStep,
          }: {
            currentStep: number;
            steps: unknown[];
            setCurrentStep: (step: number) => void;
          }) => {
            const isLastStep = currentStep === steps.length - 1;
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                }}
              >
                {/* Exit Tour Button */}
                <button
                  data-testid="tour-exit-btn"
                  onClick={handleClose}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Exit Tour
                </button>

                {/* Auto-play Controls and Manual Navigation */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {/* Manual Navigation - Always show */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        if (assistantPanelOpen) {
                          return;
                        }
                        if (currentStep > 0) {
                          // Track step back
                          const step = steps[currentStep];
                          const stepName = (
                            typeof step === 'object' &&
                            step &&
                            'content' in step &&
                            step.content &&
                            typeof step.content === 'object' &&
                            'props' in step.content &&
                            step.content.props &&
                            typeof step.content.props === 'object' &&
                            'title' in step.content.props
                              ? step.content.props.title
                              : 'Unknown Step'
                          ) as string;
                          trackTourStepBack(
                            tourName,
                            currentStep,
                            stepName,
                            'BaseTour',
                            'handleStepBack'
                          );
                          setCurrentStep(currentStep - 1);
                        }
                      }}
                      disabled={currentStep === 0}
                      style={{
                        padding: '8px',
                        backgroundColor: currentStep === 0 ? '#ccc' : '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                      }}
                      title="Previous"
                    >
                      <ArrowBackIcon fontSize="small" />
                    </button>

                    {/* Play/Pause Button with Visual Countdown - Between Previous and Next */}
                    {isAutoPlaying && (
                      <button
                        onClick={() => {
                          togglePause();
                        }}
                        style={{
                          padding: '4px',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px',
                          position: 'relative',
                        }}
                        title={isPaused ? 'Play' : 'Pause'}
                      >
                        {/* Circular Progress Background */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                          }}
                        />

                        {/* Circular Progress Fill */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            border: '2px solid transparent',
                            borderTop: '2px solid white',
                            transform: `rotate(${
                              (timeRemaining / 20) * 360 - 90
                            }deg)`,
                            transition: 'transform 1s linear',
                          }}
                        />

                        {/* Icon */}
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          {isPaused ? (
                            <PlayArrowIcon fontSize="small" />
                          ) : (
                            <PauseIcon fontSize="small" />
                          )}
                        </div>
                      </button>
                    )}

                    <button
                      onClick={() => {
                        // PA-19 — the panel is open; do not walk the tour onto it.
                        // `Exit Tour` below stays clickable, so the user is never stuck.
                        if (assistantPanelOpen) {
                          return;
                        }
                        if (isLastStep) {
                          handleTourEnd();
                        } else {
                          // Track step advance
                          const step = steps[currentStep];
                          const stepName = (
                            typeof step === 'object' &&
                            step &&
                            'content' in step &&
                            step.content &&
                            typeof step.content === 'object' &&
                            'props' in step.content &&
                            step.content.props &&
                            typeof step.content.props === 'object' &&
                            'title' in step.content.props
                              ? step.content.props.title
                              : 'Unknown Step'
                          ) as string;
                          trackTourStepAdvance(
                            tourName,
                            currentStep,
                            stepName,
                            'BaseTour',
                            'handleStepAdvance'
                          );
                          setCurrentStep(currentStep + 1);
                        }
                      }}
                      style={{
                        padding: '8px',
                        backgroundColor: isLastStep ? '#4caf50' : '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                      }}
                      title={isLastStep ? 'Finish' : 'Next'}
                    >
                      {isLastStep ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <ArrowForwardIcon fontSize="small" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          },
        }}
      >
        {children}
        <SimpleTour isOpen={isTourOpen} onClose={handleClose} />
      </TourProvider>
    </TourContext.Provider>
  );
};

export default BaseTour;
